import { ApolloError } from "apollo-boost"
import { ErrorResponse } from "apollo-link-error"
import { GraphQLError } from "graphql"
import { Maybe, ResponseErrorType } from "../../../graphql/generated-types"
import logger from "../../../server/logger"
import { isBrowser } from "../../../utils/general"
import { getRouterQuery, importRouter } from "../../../utils/router"

const Router = importRouter()

export const APOLLO_CACHE_PREFIX = "apollo-cache-persist-"

export const getApolloError = (apolloError: ApolloError | ErrorResponse, errorType: string): Maybe<GraphQLError> => {
    const graphqlError: Maybe<GraphQLError> =
        apolloError.graphQLErrors &&
        apolloError.graphQLErrors.find(
            // todo: refactor api to use just 'responseErrorType' property, not code
            (error) => error.extensions.responseErrorType === errorType || error.extensions.code === errorType
        )

    return graphqlError || null
}

export const getApolloBusinessError = (apolloError: ApolloError | ErrorResponse): Maybe<GraphQLError> => {
    const businessError: Maybe<GraphQLError> = getApolloError(apolloError, ResponseErrorType.BusinessError)

    return businessError || null
}

export const hasSpecificNetworkError = (apolloError: ApolloError | ErrorResponse, errorCode: string): boolean => {
    const networkError: Maybe<GraphQLError> =
        // @ts-ignore
        apolloError.networkError &&
        // @ts-ignore
        apolloError.networkError.result &&
        // @ts-ignore
        apolloError.networkError.result.errors &&
        // @ts-ignore
        apolloError.networkError.result.errors.find((error) => error.extensions.code === errorCode)

    return !!networkError
}

export const hasSpecificApolloError = (apolloError: ApolloError | ErrorResponse, errorCode: string): boolean =>
    !!getApolloError(apolloError, errorCode)

export const hasSpecificResponseError = (apolloError: ApolloError | ErrorResponse, errorCode: string) =>
    hasSpecificApolloError(apolloError, errorCode) || hasSpecificNetworkError(apolloError, errorCode)

export const hookLocalStorageSetItem = () => {
    // Since "apollo-cache-persist" calls the localStorage.setItem() WITHOUT the try/catch exception,
    // and we dont want to have any unhandled errors in our app - we need to do it on our own with this function hook.
    if (isBrowser && window.localStorage.setItem) {
        const _setItem = window.localStorage.setItem
        window.localStorage.setItem = function () {
            let result
            try {
                if (arguments && arguments[0] && arguments[0].includes(APOLLO_CACHE_PREFIX)) {
                    arguments[0] = fixApolloPersistKey(arguments[0])
                }
                result = _setItem.apply(this, arguments)
            } catch (e) {
                // The usual errors we are encountering are "localstorage size exceeded",
                // in such a cases we clear all the apollo-cache-persisted data.
                logger.warn("Localstorage size exceeded, clearing the apollo data from cache", e)
                Object.keys(window.localStorage).forEach((key) => {
                    if (key.includes(APOLLO_CACHE_PREFIX)) {
                        window.localStorage.removeItem(key)
                    }
                })
            }
            return result
        }
    }
}

export const hookLocalStorageGetItem = () => {
    if (isBrowser && window.localStorage.getItem) {
        const _getItem = window.localStorage.getItem
        window.localStorage.getItem = function () {
            let result
            try {
                if (arguments && arguments[0] && arguments[0].includes(APOLLO_CACHE_PREFIX)) {
                    arguments[0] = fixApolloPersistKey(arguments[0])
                }
                result = _getItem.apply(this, arguments)
            } catch (e) {
                logger.warn("Error in localstorage._getItem", e)
            }
            return result
        }
    }
}

export const composeApolloPersistKey = (prefix: string, accountSlug: string, locationSlug: string) => {
    return prefix + accountSlug + (locationSlug ? "-" + locationSlug : "")
}

export const fixApolloPersistKey = (storageKey: string) => {
    const aSlug = getRouterQuery(Router.router, "accountSlug")
    const lSlug = getRouterQuery(Router.router, "locationSlug")
    if (lSlug && !storageKey.includes(lSlug)) {
        const newKey = composeApolloPersistKey(APOLLO_CACHE_PREFIX, aSlug, lSlug)
        return newKey
    }

    return storageKey
}
